import { HttpClient } from '@angular/common/http';
import { computed, inject, Injectable, signal } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { CartItemModel, PreorderedProductModel, ProductModel } from '@app/models';

@Injectable({
    providedIn: 'root',
})
export class PreorderedProductService
{
    items = computed(() => this.preorderedItems());
    itemCount = computed(() => this.preorderedItems().reduce((total, item) => total + item.quantity, 0));
    totalPrice = computed(() => this.preorderedItems().reduce((total, item) => total + item.subtotal, 0));

    private preorderedItems = signal<CartItemModel[]>([]);
    private endPoint = '/preorderedItems';
    private http = inject(HttpClient);

    addToCart(product: ProductModel, quantity: number = 1): void
    {
        const currentItems = this.preorderedItems();
        const existingItemIndex = currentItems.findIndex((item) => item.product.id === product.id);

        if (existingItemIndex >= 0)
        {
            const updatedQuantity = currentItems[existingItemIndex].quantity + quantity;
            const updatedItems = [...currentItems];
            updatedItems[existingItemIndex] = {
                ...currentItems[existingItemIndex],
                quantity: updatedQuantity,
                subtotal: updatedQuantity * (product.price ? Number(product.price) : 0),
            };
            this.preorderedItems.set(updatedItems);
        }
        else
        {
            // Add new item
            const newItem: CartItemModel = {
                product,
                quantity,
                subtotal: quantity * (product.price ? Number(product.price) : 0),
            };
            this.preorderedItems.set([...currentItems, newItem]);
        }
    }

    removeFromCart(productId: number): void
    {
        const updatedItems = this.preorderedItems().filter(item => item.product.id !== productId);
        this.preorderedItems.set(updatedItems);
    }

    updateQuantity(productId: number, quantity: number): void
    {
        if (quantity <= 0)
        {
            this.removeFromCart(productId);

            return;
        }

        const currentItems = this.preorderedItems();
        const existingItemIndex = currentItems.findIndex((item) => item.product.id === productId);

        if (existingItemIndex >= 0)
        {
            const updatedItems = [...currentItems];
            const updatedPrice = updatedItems[existingItemIndex].product.price;
            const updatedSubtotal = quantity * (updatedPrice ? Number(updatedPrice) : 0);
            updatedItems[existingItemIndex] = {
                ...updatedItems[existingItemIndex],
                quantity,
                subtotal: updatedSubtotal,
            };
            this.preorderedItems.set(updatedItems);
        }
    }

    getItemSubtotal(productId: number): number
    {
        const item = this.preorderedItems().find(item => item.product.id === productId);

        return item ? item.subtotal : 0;
    }

    getItemQuantity(productId: number): number
    {
        const item = this.preorderedItems().find(item => item.product.id === productId);

        return item ? item.quantity : 0;
    }

    clearCart(): void
    {
        this.preorderedItems.set([]);
    }

    isInCart(productId: number): boolean
    {
        return this.preorderedItems().some((item) => item.product.id === productId);
    }

    getPreorderedProducts(): Promise<PreorderedProductModel[]>
    {
        return firstValueFrom(this.http.get<PreorderedProductModel[]>(this.endPoint));
    }
}
